<template>
  <div class="programs-archive">
    <Loading :loading="isFetching" loading-text="Loading..." background="none-background">
      <div class="content-wrapper" :class="{ 'loading': isFetching }">
        <div class="workflow-content" :class="{ 'loading': isFetching }">
          <div class="workflow-grid grid-grid" :class="{ 'is-empty': !isFetching && items.length === 0 }">
            <div v-if="!isFetching && items.length === 0" class="no-data-state">
              <img src="@/assets/MyWorkBench/archive/empty.svg" alt="Empty" class="empty-icon" />
              <p class="empty-text">You don't have any archived programs.</p>
            </div>

            <WorkflowCard
              v-for="item in items"
              :key="item.id"
              :workflow="workflowData(item)"
              :is-archive="true"
              :automation-type="'program'"
              :loading="processingItemId === item.id"
              @restore="handleRestore(item)"
              @delete="handleDelete(item)"
            />
          </div>
        </div>

        <Pagination
          v-if="items.length > 0"
          :total_items="total"
          :current_page="currentPage"
          :per_page="perPage"
          :per_page_options="[10, 20, 30]"
          @update:current_page="handlePageChange"
          @update:per_page="handlePageSizeChange"
        />
      </div>
    </Loading>

    <DeleteConfirmDialog
      v-model="deleteDialogVisible"
      title="Delete Program"
      :item-name="selectedProgram?.name"
      :confirm-action="handleProgramDelete"
      loading-text="Deleting program..."
      modal-class="delete-confirm-dialog"
    />
  </div>
</template>

<script>
import Loading from '@/NewLibrary/Loading/Loading.vue';
import WorkflowCard from '@/views/my-workbench-new/workflow/WorkflowCard.vue';
import DeleteConfirmDialog from '@/views/my-workbench-new/archive/components/DeleteConfirmDialog.vue';
import { notify } from '@/utils/notify';
import {
  getArchivedProgramsApi,
  restoreProgramApi,
  deleteProgramPermanentlyApi
} from '@/api/archive';

export default {
  name: 'ProgramsArchive',
  components: {
    Loading,
    WorkflowCard,
    DeleteConfirmDialog,
  },

  data() {
    return {
      isFetching: false,
      perPage: 10,
      currentPage: 1,
      items: [],
      total: 0,
      deleteDialogVisible: false,
      selectedProgram: null,
      processingItemId: null,
    };
  },

  watch: {
    perPage() {
      this.fetchArchivedPrograms();
    },
    currentPage() {
      this.fetchArchivedPrograms();
    },
  },

  mounted() {
    this.fetchArchivedPrograms();
  },

  methods: {
    async fetchArchivedPrograms(showLoading = true) {
      if (showLoading) this.isFetching = true;
      try {
        const response = await getArchivedProgramsApi({
          page: this.currentPage,
          limit: this.perPage,
        });
        this.items = response.data.workflows || [];
        this.total = response.data.total || 0;
      } catch (error) {
        console.error('Failed to fetch archived programs:', error);
        this.items = [];
        this.total = 0;
      } finally {
        if (showLoading) this.isFetching = false;
      }
    },

    workflowData(item) {
      return {
        id: item.id,
        name: item.name,
        description: item.description,
        type: 'program',
        status: 'archived',
        updated_at: item.updated_at,
        is_program: true,
      };
    },

    async handleRestore(item) {
      if (this.processingItemId) return;
      try {
        this.processingItemId = item.id;
        await restoreProgramApi(item.id);
        notify({ type: 'success', message: `"${item.name}" has been restored` });
        await this.fetchArchivedPrograms(false);
      } catch (error) {
        console.error('Failed to restore program:', error);
        notify({ type: 'error', message: `Failed to restore "${item.name}"` });
      } finally {
        this.processingItemId = null;
      }
    },

    handleDelete(item) {
      this.selectedProgram = item;
      this.deleteDialogVisible = true;
    },

    async handleProgramDelete() {
      try {
        await deleteProgramPermanentlyApi(this.selectedProgram.id);
        notify({
          type: 'success',
          title: 'Program Deleted',
          message: `"${this.selectedProgram.name}" has been permanently deleted.`
        });
        await this.fetchArchivedPrograms(false);
      } catch (error) {
        console.error('Error deleting program:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete program. Please try again.'
        });
        throw error;
      } finally {
        this.selectedProgram = null;
      }
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.programs-archive {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  height: 100%;
  /* padding: 24px 24px 24px 0; */
  min-height: 0;
  overflow: hidden;
}

.workflow-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
}

.workflow-content::-webkit-scrollbar {
  width: 6px;
}

.workflow-content::-webkit-scrollbar-track {
  background: transparent;
}

.workflow-content::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.workflow-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.workflow-content.loading {
  opacity: 0.3;
  pointer-events: none;
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  grid-column: 1 / -1;
  min-height: 100%;
  width: 100%;
  text-align: center;
}

.empty-icon {
  width: 350px;
  height: 230px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #333;
  margin: 0;
}

.workflow-grid {
  display: grid;
  gap: 12px;
  flex: 1;
  min-height: 100%;
  align-content: start;
}

.workflow-grid.is-empty {
  align-content: stretch;
}

.workflow-grid.grid-grid {
  grid-template-columns: repeat(2, 1fr);
}



:deep(.medication-pagination) {
  flex-shrink: 0;
}
</style>
